import * as Plot from '@observablehq/plot';
import {useEffect, useRef} from 'react';
import {ParsedGraphConfig, YAxisConfig} from '../../types/GraphTypes';

interface GraphBarChartProps {
  chartData: Record<string, number | string | null>[];
  parsedConfig: ParsedGraphConfig;
  colors: string[];
  xLabel: string;
  getYDomain: (config: YAxisConfig) => [number | 'dataMin', number | 'dataMax'];
}

export default function GraphBarChart({chartData, parsedConfig, colors, xLabel, getYDomain}: GraphBarChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const {yAxes, dataSeries} = parsedConfig;

  useEffect(() => {
    if (!containerRef.current || chartData.length === 0) return;

    // Prepare data for Observable Plot
    const plotData = chartData.map((point) => {
      const result: Record<string, number | string> = {x: String(point.x)};
      dataSeries.forEach((series) => {
        const value = point[series.key];
        if (value !== null && value !== undefined) {
          result[series.key] = Number(value);
        }
      });
      return result;
    });

    // For multiple Y axes, we'll use the first one for now
    const primaryYAxis = yAxes[0];
    const primaryYDomain = primaryYAxis ? getYDomain(primaryYAxis.config) : undefined;
    const yMin = primaryYDomain?.[0] === 'dataMin' ? undefined : (primaryYDomain?.[0] as number | undefined);
    const yMax = primaryYDomain?.[1] === 'dataMax' ? undefined : (primaryYDomain?.[1] as number | undefined);

    // Build marks
    const marks: Plot.Markish[] = [
      Plot.gridX({stroke: '#e0e0e0', strokeDasharray: '3,3'}),
      Plot.gridY({stroke: '#e0e0e0', strokeDasharray: '3,3'}),
    ];

    // Add bar marks for each series
    // For multiple series, render them as separate layers (they'll overlap, but Observable Plot handles this)
    dataSeries.forEach((series, index) => {
      const color = colors[index % colors.length];
      const seriesData = plotData.filter((d) => d[series.key] !== null && d[series.key] !== undefined);
      marks.push(
        Plot.barY(seriesData, {
          x: 'x',
          y: series.key,
          fill: color,
        }),
      );
    });

    // Create the plot
    const plotOptions: Plot.PlotOptions = {
      marks,
      x: {
        label: xLabel,
        type: 'band',
      },
      y: {
        label: primaryYAxis?.config.label || 'Y Axis',
        domain: yMin !== undefined || yMax !== undefined ? [yMin ?? undefined, yMax ?? undefined] : undefined,
        type: 'linear',
      },
      marginTop: 5,
      marginRight: 30,
      marginBottom: 20,
      marginLeft: 80,
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
    };

    const chart = Plot.plot(plotOptions);
    const container = containerRef.current;
    container.innerHTML = '';
    container.appendChild(chart);

    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [chartData, parsedConfig, colors, xLabel, getYDomain, yAxes, dataSeries]);

  return <div ref={containerRef} data-testid="graph-bar-chart" style={{width: '100%', height: '100%'}} />;
}
